import { jsPDF } from 'jspdf';
import { PaymentAgreement } from '@/components/dashboard/agreements/PaymentAgreementTypes';
import { toast } from 'sonner';

/**
 * Servicio profesional para generación de Contratos y Acuerdos de Pago en formato PDF
 * con plena validez probatoria bajo la Ley 675 de 2001 (Propiedad Horizontal),
 * Art. 2469 y ss. del Código Civil (Transacción Extrajudicial) y Ley 527 de 1999 (Firmas Electrónicas).
 */
export class PaymentAgreementPdfService {
  /**
   * Genera el documento PDF formal del acuerdo de pago
   */
  public static generateAgreementPdf(agreement: PaymentAgreement): jsPDF {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter', // 215.9 x 279.4 mm
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 18;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    // Colores institucionales
    const primaryColor: [number, number, number] = [16, 55, 92]; // Navy Blue elegante
    const accentColor: [number, number, number] = [5, 150, 105]; // Emerald Green
    const textColor: [number, number, number] = [30, 41, 59]; // Slate 800
    const lightBg: [number, number, number] = [248, 250, 252]; // Slate 50
    const borderColor: [number, number, number] = [203, 213, 225]; // Slate 300

    // Función auxiliar para verificar salto de página
    const checkPageBreak = (neededHeight: number) => {
      if (y + neededHeight > pageHeight - margin - 15) {
        doc.addPage();
        y = margin;
        renderHeader(true);
      }
    };

    // Renderizado del encabezado institucional
    const renderHeader = (isContinuation: boolean = false) => {
      // Franja superior de acento
      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, pageWidth, 6, 'F');
      doc.setFillColor(...accentColor);
      doc.rect(0, 6, pageWidth, 1.5, 'F');

      y = margin + 2;

      const logoImg = agreement.copropiedad_logo_data || agreement.copropiedad_logo_url;
      let textStartX = margin;
      if (logoImg && !isContinuation) {
        try {
          const logoW = 16;
          const logoH = 13;
          doc.addImage(logoImg, 'PNG', margin, y - 2, logoW, logoH, undefined, 'FAST');
          textStartX = margin + logoW + 3;
        } catch (imgErr) {
          console.warn('[PDF] Error al renderizar logo de copropiedad en acuerdo:', imgErr);
        }
      }

      // Encabezado institucional
      doc.setTextColor(...primaryColor);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(textStartX > margin ? 11.5 : 13);
      doc.text((agreement.copropiedad_nombre || 'CONJUNTO RESIDENCIAL').toUpperCase(), textStartX, y + (textStartX > margin ? 1 : 0));

      doc.setFontSize(textStartX > margin ? 7.5 : 8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      y += 4;
      doc.text(`NIT: ${agreement.copropiedad_nit || 'Régimen Ley 675 de 2001'} | Representación Legal y Administración de Copropiedad`, textStartX, y + (textStartX > margin ? 1.5 : 0));

      // Folio / Radicado a la derecha
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...primaryColor);
      doc.setFontSize(9);
      doc.text(`RADICADO: ${agreement.id}`, pageWidth - margin, y - 4, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(100, 116, 139);
      const fechaDoc = agreement.created_at
        ? new Date(agreement.created_at).toLocaleDateString('es-CO', { year: 'numeric', month: 'short', day: 'numeric' })
        : new Date().toLocaleDateString('es-CO');
      doc.text(`Fecha: ${fechaDoc} ${isContinuation ? '(Cont.)' : ''}`, pageWidth - margin, y, { align: 'right' });

      y += 5;
      doc.setDrawColor(...borderColor);
      doc.setLineWidth(0.4);
      doc.line(margin, y, pageWidth - margin, y);
      y += 6;
    };

    // 1. Renderizar encabezado en página 1
    renderHeader(false);

    // 2. Título Principal del Contrato
    doc.setFillColor(...lightBg);
    doc.roundedRect(margin, y, contentWidth, 15, 2, 2, 'FD');
    doc.setDrawColor(...borderColor);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(...primaryColor);
    doc.text('CONTRATO DE TRANSACCIÓN EXTRAJUDICIAL Y ACUERDO DE PAGO DE CARTERA', pageWidth / 2, y + 6, { align: 'center' });
    
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...accentColor);
    doc.text('TÍTULO EJECUTIVO PLENO - ART. 2469 CÓDIGO CIVIL & ART. 48 LEY 675 DE 2001', pageWidth / 2, y + 11, { align: 'center' });
    y += 19;

    // 3. Identificación de las partes (Tabla / Bloque)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...primaryColor);
    doc.text('I. IDENTIFICACIÓN DE LAS PARTES', margin, y);
    y += 3.5;

    const blockHeight = 25;
    doc.setFillColor(...lightBg);
    doc.setDrawColor(...borderColor);
    doc.roundedRect(margin, y, contentWidth, blockHeight, 1.5, 1.5, 'FD');

    // Columna 1: Copropiedad Acreedora
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...primaryColor);
    doc.text('LA COPROPIEDAD (ACREEDORA):', margin + 3, y + 5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...textColor);
    doc.text(`Nombre: ${agreement.copropiedad_nombre}`, margin + 3, y + 9);
    doc.text(`NIT: ${agreement.copropiedad_nit || 'No Registrado'}`, margin + 3, y + 13);
    doc.text(`Representante: ${agreement.administrador_nombre || 'Administración de Copropiedad'}`, margin + 3, y + 17);
    doc.text('Domicilio: República de Colombia', margin + 3, y + 21);

    // Línea divisoria vertical
    const midX = margin + contentWidth / 2;
    doc.setDrawColor(...borderColor);
    doc.line(midX, y + 2, midX, y + blockHeight - 2);

    // Columna 2: Copropietario Deudor
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text('EL COPROPIETARIO (DEUDOR):', midX + 3, y + 5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...textColor);
    doc.text(`Nombre: ${agreement.propietario_nombre}`, midX + 3, y + 9);
    doc.text(`C.C. / NIT: ${agreement.propietario_id || 'Verificado'}`, midX + 3, y + 13);
    doc.text(`Inmueble / Unidad: ${agreement.unidad_privada}`, midX + 3, y + 17);
    doc.text(`Contacto: Tel. ${agreement.telefono || 'N/A'} - ${agreement.email || 'N/A'}`, midX + 3, y + 21);

    y += blockHeight + 6;

    // 4. Liquidación y Beneficio de Condonación
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...primaryColor);
    doc.text('II. LIQUIDACIÓN DEL SALDO Y BENEFICIO DE CONDONACIÓN CONDICIONADA', margin, y);
    y += 3.5;

    const summaryCardHeight = 22;
    doc.setFillColor(240, 253, 244); // Emerald 50
    doc.setDrawColor(167, 243, 208); // Emerald 200
    doc.roundedRect(margin, y, contentWidth, summaryCardHeight, 1.5, 1.5, 'FD');

    const colW = contentWidth / 4;
    // Caja 1: Deuda Original
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text('Deuda Original Acumulada', margin + 4, y + 6);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...textColor);
    doc.text(`$${agreement.deuda_total_inicial.toLocaleString('es-CO')}`, margin + 4, y + 12);
    doc.setFontSize(6.5);
    doc.text(`(Cap: $${agreement.capital_original.toLocaleString('es-CO')})`, margin + 4, y + 17);

    // Caja 2: Condonación
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(5, 150, 105);
    doc.text(`Condonación Intereses (${agreement.porcentaje_condonacion_intereses}%)`, margin + colW + 4, y + 6);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(5, 150, 105);
    doc.text(`-$${agreement.intereses_condonados.toLocaleString('es-CO')}`, margin + colW + 4, y + 12);
    doc.setFontSize(6.5);
    doc.text('Beneficio Condicionado', margin + colW + 4, y + 17);

    // Caja 3: Cuota Inicial
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(180, 83, 9);
    doc.text(`Cuota Inicial (${agreement.cuota_inicial_porcentaje}%)`, margin + colW * 2 + 4, y + 6);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(180, 83, 9);
    doc.text(`$${agreement.cuota_inicial_monto.toLocaleString('es-CO')}`, margin + colW * 2 + 4, y + 12);
    doc.setFontSize(6.5);
    doc.text(`Límite: ${agreement.cuota_inicial_fecha_limite}`, margin + colW * 2 + 4, y + 17);

    // Caja 4: Total Neto Pactado
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...primaryColor);
    doc.text('TOTAL PACTADO A PAGAR', margin + colW * 3 + 4, y + 6);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(...primaryColor);
    doc.text(`$${agreement.deuda_pactada.toLocaleString('es-CO')}`, margin + colW * 3 + 4, y + 12);
    doc.setFontSize(6.5);
    doc.setTextColor(...accentColor);
    doc.text(`${agreement.numero_cuotas} Cuotas de $${agreement.valor_cuota_mensual.toLocaleString('es-CO')}`, margin + colW * 3 + 4, y + 17);

    y += summaryCardHeight + 6;

    // 5. Tabla Oficial de Amortización de Cuotas
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...primaryColor);
    doc.text('III. CRONOGRAMA OFICIAL DE PAGOS Y AMORTIZACIÓN', margin, y);
    y += 3.5;

    // Encabezado de la tabla de cuotas
    const tableHeaderHeight = 6;
    doc.setFillColor(...primaryColor);
    doc.rect(margin, y, contentWidth, tableHeaderHeight, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(255, 255, 255);

    const c1 = margin + 3;
    const c2 = margin + 28;
    const c3 = margin + 65;
    const c4 = margin + 105;
    const c5 = margin + 140;

    doc.text('CONCEPTO / CUOTA', c1, y + 4.2);
    doc.text('FECHA VENCIMIENTO', c2, y + 4.2);
    doc.text('VALOR CUOTA ($ COP)', c3, y + 4.2);
    doc.text('ESTADO', c4, y + 4.2);
    doc.text('REFERENCIA / RECIBO', c5, y + 4.2);
    y += tableHeaderHeight;

    // Fila de Cuota Inicial
    const rowHeight = 5.2;
    doc.setFillColor(254, 243, 199); // Amber 100
    doc.rect(margin, y, contentWidth, rowHeight, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(146, 64, 14);
    doc.text(`Cuota Inicial (${agreement.cuota_inicial_porcentaje}%)`, c1, y + 3.7);
    doc.setFont('helvetica', 'normal');
    doc.text(agreement.cuota_inicial_fecha_limite || 'Inmediato', c2, y + 3.7);
    doc.setFont('helvetica', 'bold');
    doc.text(`$${agreement.cuota_inicial_monto.toLocaleString('es-CO')}`, c3, y + 3.7);
    doc.text(agreement.cuota_inicial_pagada ? 'PAGADA' : 'PENDIENTE', c4, y + 3.7);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.text(agreement.cuota_inicial_referencia || 'Pendiente de acreditación', c5, y + 3.7);
    y += rowHeight;

    // Filas de Cuotas Financiadas
    (agreement.cuotas_detalle || []).forEach((cuota, idx) => {
      checkPageBreak(rowHeight + 2);
      doc.setFillColor(idx % 2 === 0 ? 255 : 248, idx % 2 === 0 ? 255 : 250, idx % 2 === 0 ? 255 : 252);
      doc.rect(margin, y, contentWidth, rowHeight, 'F');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...textColor);
      doc.text(`Cuota #${cuota.numero} de ${agreement.numero_cuotas}`, c1, y + 3.7);
      doc.text(cuota.fecha_vencimiento, c2, y + 3.7);
      doc.setFont('helvetica', 'bold');
      doc.text(`$${cuota.total_cuota.toLocaleString('es-CO')}`, c3, y + 3.7);

      if (cuota.estado === 'pagada') {
        doc.setTextColor(5, 150, 105);
        doc.text('PAGADA', c4, y + 3.7);
      } else if (cuota.estado === 'vencida') {
        doc.setTextColor(225, 29, 72);
        doc.text('EN MORA', c4, y + 3.7);
      } else {
        doc.setTextColor(100, 116, 139);
        doc.text('PENDIENTE', c4, y + 3.7);
      }

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(100, 116, 139);
      doc.text(cuota.referencia_pago ? `Ref: ${cuota.referencia_pago}` : 'En espera', c5, y + 3.7);

      y += rowHeight;
    });

    // Línea inferior de la tabla
    doc.setDrawColor(...borderColor);
    doc.line(margin, y, pageWidth - margin, y);
    y += 5;

    // 6. Cláusulas Legales vinculantes
    checkPageBreak(40);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...primaryColor);
    doc.text('IV. CLÁUSULAS JURÍDICAS Y FUERZA EJECUTIVA', margin, y);
    y += 3.5;

    const clauses = [
      'PRIMERA. RECONOCIMIENTO DE LA OBLIGACIÓN: EL COPROPIETARIO DEUDOR reconoce de manera voluntaria, clara, expresa y exigible que adeuda a LA COPROPIEDAD las sumas liquidadas en el presente acuerdo por expensas comunes de administración e intereses causados.',
      `SEGUNDA. BENEFICIO CONDICIONADO DE CONDONACIÓN: LA COPROPIEDAD autoriza condonar el ${agreement.porcentaje_condonacion_intereses}% de intereses ($${agreement.intereses_condonados.toLocaleString('es-CO')} COP). Esta condonación queda supeditada al pago estricto y puntual de cada una de las cuotas pactadas.`,
      'TERCERA. CLÁUSULA ACELERATORIA Y MÉRITO EJECUTIVO: El incumplimiento en el pago de cualquiera de las cuotas en la fecha límite fijada causará la pérdida automática del descuento en intereses, haciéndose exigible la totalidad de la deuda original acumulada, prestando mérito ejecutivo pleno (Art. 422 CGP y Art. 48 Ley 675/2001) para iniciar embargo inmediato sin previo requerimiento.',
      'CUARTA. EFECTOS DE COSA JUZGADA: El presente acuerdo produce los efectos de la cosa juzgada de conformidad con el artículo 2483 del Código Civil Colombiano, impidiendo formular reclamos posteriores sobre las sumas objeto de transacción.',
      'QUINTA. VALIDEZ PROBATORIA ELECTRÓNICA: Las partes convienen que el intercambio, firma electrónica o aceptación mediante OTP o canal digital goza de plena eficacia jurídica probatoria conforme a la Ley 527 de 1999.'
    ];

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.8);
    doc.setTextColor(...textColor);

    clauses.forEach((cl) => {
      checkPageBreak(12);
      const splitText = doc.splitTextToSize(cl, contentWidth);
      doc.text(splitText, margin, y);
      y += splitText.length * 3.3 + 1.2;
    });

    y += 3;

    // 7. Bloque de Firmas y Estampilla Digital
    checkPageBreak(38);
    const signBoxHeight = 30;
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.3);
    doc.line(margin, y, pageWidth - margin, y);
    y += 4;

    const signW = (contentWidth - 10) / 2;

    // Firma 1: Administración
    doc.setDrawColor(148, 163, 184);
    doc.line(margin + 5, y + 16, margin + signW - 5, y + 16);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...primaryColor);
    doc.text(agreement.administrador_nombre || 'ADMINISTRACIÓN Y REP. LEGAL', margin + 5, y + 20);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139);
    doc.text(`En representación de ${agreement.copropiedad_nombre}`, margin + 5, y + 23.5);
    doc.text(`NIT: ${agreement.copropiedad_nit || 'Registrado'}`, margin + 5, y + 27);

    // Firma 2: Copropietario / Deudor
    const sign2X = margin + signW + 10;
    doc.line(sign2X + 5, y + 16, sign2X + signW - 5, y + 16);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...primaryColor);
    doc.text(agreement.propietario_nombre.toUpperCase(), sign2X + 5, y + 20);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139);
    doc.text(`C.C. / NIT: ${agreement.propietario_id || 'Registrado'}`, sign2X + 5, y + 23.5);
    doc.text(`Unidad Privada: ${agreement.unidad_privada}`, sign2X + 5, y + 27);

    // Si está firmado digitalmente, agregar sello digital visual
    if (agreement.firmado_digitalmente_at || agreement.estado === 'al_dia' || agreement.estado === 'firmado_activo' || agreement.cuota_inicial_pagada) {
      doc.setFillColor(240, 253, 244);
      doc.setDrawColor(16, 185, 129);
      doc.roundedRect(sign2X + 5, y + 2, signW - 10, 10, 1, 1, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(5, 150, 105);
      doc.text('FIRMADO ELECTRÓNICAMENTE', sign2X + (signW - 10) / 2 + 5, y + 6, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(5.5);
      doc.text(`OTP Token: ${agreement.magic_token?.slice(0, 18)}... | Ley 527 de 1999`, sign2X + (signW - 10) / 2 + 5, y + 9.5, { align: 'center' });
    }

    y += signBoxHeight + 2;

    // Pie de página de seguridad en todas las páginas
    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(148, 163, 184);
      doc.setDrawColor(...borderColor);
      doc.line(margin, pageHeight - 10, pageWidth - margin, pageHeight - 10);
      const portalUrl = this.buildDebtorPortalUrl(agreement);
      doc.text(
        `Portal de Pagos: ${portalUrl} | Token Seguro: ${agreement.magic_token || 'N/A'}`,
        margin,
        pageHeight - 6.5
      );
      doc.text(`Página ${p} de ${totalPages}`, pageWidth - margin, pageHeight - 6.5, { align: 'right' });
    }

    return doc;
  }

  /**
   * Genera el slug institucional del nombre del conjunto para la URL amigable
   * Ej: "Conjunto Residencial Torres del Parque" -> "torres-del-parque"
   */
  public static slugifyConjunto(name?: string): string {
    if (!name) return 'copropiedad';
    const simplified = name
      .toLowerCase()
      .replace(/^(conjunto residencial|conjunto|edificio|condominio|urbanizaci[oó]n|agrupaci[oó]n)\s+/i, '')
      .trim();

    const target = simplified || name;
    return target
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'copropiedad';
  }

  /**
   * Genera el Certificado Oficial de Paz y Salvo en formato PDF
   * conforme a la Ley 675 de 2001 (Arts. 29, 48 y 51) y Art. 1625 del Código Civil
   */
  public static generatePazYSalvoPdf(agreement: PaymentAgreement, customDate?: string): jsPDF {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter', // 215.9 x 279.4 mm
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 18;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    // Colores institucionales
    const primaryColor: [number, number, number] = [16, 55, 92]; // Navy Blue elegante
    const accentColor: [number, number, number] = [5, 150, 105]; // Emerald Green
    const goldColor: [number, number, number] = [180, 83, 9]; // Amber Gold
    const textColor: [number, number, number] = [30, 41, 59]; // Slate 800
    const lightBg: [number, number, number] = [248, 250, 252]; // Slate 50
    const borderColor: [number, number, number] = [203, 213, 225]; // Slate 300

    // Franja superior de acento (Esmeralda + Azul Marino)
    doc.setFillColor(...accentColor);
    doc.rect(0, 0, pageWidth, 5, 'F');
    doc.setFillColor(...primaryColor);
    doc.rect(0, 5, pageWidth, 2.5, 'F');

    y = margin + 2;

    const logoImg = agreement.copropiedad_logo_data || agreement.copropiedad_logo_url;
    let textStartX = margin;
    if (logoImg) {
      try {
        const logoW = 16;
        const logoH = 13;
        doc.addImage(logoImg, 'PNG', margin, y - 2, logoW, logoH, undefined, 'FAST');
        textStartX = margin + logoW + 3;
      } catch (imgErr) {
        console.warn('[PDF] Error al renderizar logo de copropiedad en paz y salvo:', imgErr);
      }
    }

    // Encabezado institucional
    doc.setTextColor(...primaryColor);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(textStartX > margin ? 11.5 : 13);
    doc.text((agreement.copropiedad_nombre || 'CONJUNTO RESIDENCIAL').toUpperCase(), textStartX, y + (textStartX > margin ? 1 : 0));

    doc.setFontSize(textStartX > margin ? 7.5 : 8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    y += 4.5;
    doc.text(`NIT: ${agreement.copropiedad_nit || 'Régimen Ley 675 de 2001'} | Personería Jurídica y Administración`, textStartX, y + (textStartX > margin ? 1.5 : 0));

    // Folio / Radicado único de Paz y Salvo a la derecha
    const cleanAgrId = agreement.id.replace(/^AGR-/, '');
    const currentYear = new Date().getFullYear();
    const radicadoPYS = `PYS-${cleanAgrId}-${currentYear}`;

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...accentColor);
    doc.setFontSize(9.5);
    doc.text(`RADICADO: ${radicadoPYS}`, pageWidth - margin, y - 4, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    const fechaDoc = customDate || new Date().toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    doc.text(`Fecha de expedición: ${fechaDoc}`, pageWidth - margin, y, { align: 'right' });

    y += 5.5;
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.4);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;

    // Título Principal del Certificado
    doc.setFillColor(240, 253, 244); // Emerald 50
    doc.setDrawColor(167, 243, 208); // Emerald 200
    doc.roundedRect(margin, y, contentWidth, 16, 2, 2, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...primaryColor);
    doc.text('CERTIFICADO OFICIAL DE PAZ Y SALVO', pageWidth / 2, y + 6.5, { align: 'center' });

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...accentColor);
    doc.text('RÉGIMEN DE PROPIEDAD HORIZONTAL — LEY 675 DE 2001 (ARTS. 29 Y 51)', pageWidth / 2, y + 11.5, { align: 'center' });

    y += 22;

    // Texto de Certificación - Administrador
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...textColor);

    const adminNombre = agreement.administrador_nombre || 'La Administración';
    const copropiedad = (agreement.copropiedad_nombre || 'La Copropiedad').toUpperCase();
    const nitText = agreement.copropiedad_nit ? `identificada con NIT No. ${agreement.copropiedad_nit}` : 'legalmente constituida bajo Ley 675 de 2001';

    const textoPreambulo = `El suscrito Administrador(a) y Representante Legal de ${copropiedad}, ${nitText}, en ejercicio de las funciones y atribuciones legales conferidas por el Artículo 51 de la Ley 675 de 2001 y el Reglamento de Propiedad Horizontal:`;
    
    const linesPreambulo = doc.splitTextToSize(textoPreambulo, contentWidth);
    doc.text(linesPreambulo, margin, y);
    y += linesPreambulo.length * 4.2 + 4;

    // Título Central: CERTIFICA
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(...primaryColor);
    doc.text('CERTIFICA:', pageWidth / 2, y, { align: 'center' });
    y += 5.5;

    // Cuerpo de la Certificación
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...textColor);

    const textoCertifica = `Que la unidad privada identificada como ${agreement.unidad_privada.toUpperCase()}, de propiedad de ${agreement.propietario_nombre.toUpperCase()}, identificado(a) con C.C. / NIT No. ${agreement.propietario_id || 'Registrado'}:`;
    const linesCertifica = doc.splitTextToSize(textoCertifica, contentWidth);
    doc.text(linesCertifica, margin, y);
    y += linesCertifica.length * 4.2 + 3;

    // Puntos certificados
    const puntos = [
      `1. Suscribió formalmente con la copropiedad el Contrato de Transacción Extrajudicial y Acuerdo de Pago Radicado No. ${agreement.id}.`,
      `2. Canceló en su totalidad las cuotas pactadas (${agreement.cuotas_totales} de ${agreement.cuotas_totales} cuotas), acreditando un recaudo acumulado y verificado de $${agreement.deuda_pactada.toLocaleString('es-CO')} COP.`,
      `3. A la fecha y hora de emisión del presente certificado, se encuentra formalmente A PAZ Y SALVO por concepto de expensas comunes ordinarias, extraordinarias, intereses moratorios y sanciones objeto del acuerdo, extinguiéndose la obligación por pago efectivo conforme al Artículo 1625 del Código Civil.`
    ];

    puntos.forEach(punto => {
      const lines = doc.splitTextToSize(punto, contentWidth - 4);
      doc.text(lines, margin + 2, y);
      y += lines.length * 4.2 + 2;
    });

    y += 3;

    // Cuadro Resumen de Obligación Liquidada
    const boxHeight = 27;
    doc.setFillColor(...lightBg);
    doc.setDrawColor(...borderColor);
    doc.roundedRect(margin, y, contentWidth, boxHeight, 2, 2, 'FD');

    // Título del cuadro
    doc.setFillColor(...primaryColor);
    doc.roundedRect(margin, y, contentWidth, 5.5, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(255, 255, 255);
    doc.text('LIQUIDACIÓN Y BALANCE FINAL DEL ACUERDO DE PAGO', margin + 4, y + 3.8);

    const rowY1 = y + 10;
    const colW = contentWidth / 4;

    // Columna 1: Acuerdo & Unidad
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139);
    doc.text('RADICADO Y UNIDAD', margin + 4, rowY1);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...primaryColor);
    doc.text(agreement.id, margin + 4, rowY1 + 4.5);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text(agreement.unidad_privada, margin + 4, rowY1 + 9);

    // Columna 2: Total Pactado
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139);
    doc.text('VALOR TOTAL PACTADO', margin + colW + 4, rowY1);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...textColor);
    doc.text(`$${agreement.deuda_pactada.toLocaleString('es-CO')}`, margin + colW + 4, rowY1 + 4.5);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text(`Condonación: $${agreement.intereses_condonados.toLocaleString('es-CO')}`, margin + colW + 4, rowY1 + 9);

    // Columna 3: Cuotas Canceladas
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139);
    doc.text('CUOTAS CANCELADAS', margin + colW * 2 + 4, rowY1);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...accentColor);
    doc.text(`${agreement.cuotas_totales} de ${agreement.cuotas_totales} (100%)`, margin + colW * 2 + 4, rowY1 + 4.5);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text('Acreditadas en Tesorería', margin + colW * 2 + 4, rowY1 + 9);

    // Columna 4: Saldo Actual Exigible
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139);
    doc.text('SALDO ACTUAL EXIGIBLE', margin + colW * 3 + 4, rowY1);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...accentColor);
    doc.text('$0 COP', margin + colW * 3 + 4, rowY1 + 4.5);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.text('EXTINGUIDO', margin + colW * 3 + 4, rowY1 + 9);

    y += boxHeight + 6;

    // Nota de Alcance Legal (Art. 29 Ley 675)
    doc.setFillColor(254, 252, 232); // Amber 50
    doc.setDrawColor(253, 230, 138); // Amber 200
    doc.roundedRect(margin, y, contentWidth, 14, 1.5, 1.5, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...goldColor);
    doc.text('ALCANCE Y EFECTOS JURÍDICOS (ART. 29 Y 51 LEY 675 DE 2001):', margin + 3, y + 4.2);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(115, 65, 0);
    const notaLegal = 'El presente certificado da fe de la inexistencia de saldos en mora por los conceptos aquí detallados a la fecha de su expedición. No exonera al propietario del pago de las expensas comunes ordinarias o extraordinarias causadas con posterioridad a la fecha de expedición.';
    const linesNota = doc.splitTextToSize(notaLegal, contentWidth - 6);
    doc.text(linesNota, margin + 3, y + 8);

    y += 18;

    // Bloque de Firmas y Sello de Verificación Digital
    const signBoxHeight = 36;
    const signW = (contentWidth - 10) / 2;

    // Firma 1: Administrador(a) y Representante Legal
    doc.setFillColor(...lightBg);
    doc.setDrawColor(...borderColor);
    doc.roundedRect(margin, y, signW, signBoxHeight, 1.5, 1.5, 'FD');

    doc.line(margin + 5, y + 18, margin + signW - 5, y + 18);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...primaryColor);
    doc.text(adminNombre.toUpperCase(), margin + 5, y + 22.5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139);
    doc.text(`Administrador(a) y Representante Legal`, margin + 5, y + 26.5);
    doc.text(`${agreement.copropiedad_nombre} | NIT: ${agreement.copropiedad_nit || 'Registrado'}`, margin + 5, y + 30);
    if (agreement.administrador_telefono || agreement.administrador_email) {
      doc.text(`Contacto: ${agreement.administrador_telefono || ''} ${agreement.administrador_email || ''}`, margin + 5, y + 33.5);
    }

    // Sello Digital de Verificación y Seguridad Ley 527 de 1999
    const sealX = margin + signW + 10;
    doc.setFillColor(240, 253, 244); // Emerald 50
    doc.setDrawColor(16, 185, 129); // Emerald 500
    doc.roundedRect(sealX, y, signW, signBoxHeight, 1.5, 1.5, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...accentColor);
    doc.text('SELLO DIGITAL DE CERTIFICACIÓN', sealX + signW / 2, y + 7, { align: 'center' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...primaryColor);
    doc.text('PAZ Y SALVO VÁLIDO Y VERIFICADO', sealX + signW / 2, y + 13, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139);
    doc.text(`Radicado: ${radicadoPYS}`, sealX + signW / 2, y + 18, { align: 'center' });
    doc.text(`Token de Seguridad: ${agreement.magic_token?.slice(0, 22) || 'AUTORIZADO-LEGALIA'}...`, sealX + signW / 2, y + 22.5, { align: 'center' });
    doc.text('Emitido bajo estándares de la Ley 527 de 1999', sealX + signW / 2, y + 27, { align: 'center' });
    doc.text('Validez probatoria plena ante Notarías y Entidades', sealX + signW / 2, y + 31, { align: 'center' });

    // Pie de página de seguridad
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(148, 163, 184);
    doc.setDrawColor(...borderColor);
    doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);

    doc.text(
      `Certificado Oficial de Paz y Salvo PH | Radicado ${radicadoPYS} | Copropiedad: ${agreement.copropiedad_nombre}`,
      margin,
      pageHeight - 7.5
    );
    doc.text('Página 1 de 1', pageWidth - margin, pageHeight - 7.5, { align: 'right' });

    return doc;
  }

  /**
   * Descarga el Certificado Oficial de Paz y Salvo en el navegador
   */
  public static downloadPazYSalvoPdf(agreement: PaymentAgreement, customDate?: string): void {
    try {
      const doc = this.generatePazYSalvoPdf(agreement, customDate);
      const safeUnit = (agreement.unidad_privada || 'unidad').replace(/\s+/g, '_').toLowerCase();
      const cleanAgrId = agreement.id.replace(/^AGR-/, '');
      const fileName = `Paz_y_Salvo_${safeUnit}_${cleanAgrId}.pdf`;
      doc.save(fileName);
      toast.success(`Certificado de Paz y Salvo descargado con éxito: ${fileName}`);
    } catch (err) {
      console.error('Error generating Paz y Salvo PDF:', err);
      toast.error('No se pudo generar el Certificado de Paz y Salvo.');
    }
  }

  /**
   * Envía por WhatsApp la confirmación de Paz y Salvo con felicitación formal al propietario
   */
  public static sharePazYSalvoViaWhatsApp(agreement: PaymentAgreement, customPhone?: string): void {
    const targetPhone = customPhone || agreement.telefono || '';
    const cleanTel = targetPhone.replace(/\D/g, '');
    const num = cleanTel.length === 10 ? `57${cleanTel}` : cleanTel;

    const portalLink = this.buildDebtorPortalUrl(agreement);
    const cleanAgrId = agreement.id.replace(/^AGR-/, '');
    const currentYear = new Date().getFullYear();
    const radicadoPYS = `PYS-${cleanAgrId}-${currentYear}`;

    const texto = `🏛️ *ADMINISTRACIÓN ${agreement.copropiedad_nombre.toUpperCase()}*\n` +
      `NIT: ${agreement.copropiedad_nit || 'Propiedad Horizontal'}\n\n` +
      `🎉 *¡FELICITACIONES! SU UNIDAD SE ENCUENTRA A PAZ Y SALVO*\n\n` +
      `Estimado(a) *${agreement.propietario_nombre}*,\n\n` +
      `Nos complace informarle que ha completado satisfactoriamente el *100% de las cuotas* de su acuerdo de pago para el inmueble *${agreement.unidad_privada}*.\n\n` +
      `📜 *Certificado Oficial de Paz y Salvo:* ${radicadoPYS}\n` +
      `💰 *Total Cancelado:* $${agreement.deuda_pactada.toLocaleString('es-CO')} COP\n` +
      `📉 *Saldo Actual:* *$0 COP (CERO PESOS)*\n` +
      `⚖️ *Estado:* *OBLIGACIÓN EXTINGUIDA POR PAGO EFECTIVO*\n\n` +
      `Descargue su Certificado Oficial en PDF directamente desde el Portal del Propietario:\n` +
      `${portalLink}\n\n` +
      `Agradecemos su compromiso con la copropiedad.\n` +
      `_Ley 675 de 2001 (Arts. 29 y 51) y Ley 527 de 1999._`;

    const waUrl = `https://wa.me/${num}?text=${encodeURIComponent(texto)}`;
    window.open(waUrl, '_blank');
    toast.success('Abriendo WhatsApp para remitir la certificación de Paz y Salvo.');
  }

  /**
   * Construye la URL canónica del portal del deudor incluyendo el nombre del conjunto
   * Ej: http://localhost:8080/portal-deudor/torres-del-parque/tok_carlos_restrepo_t2_504
   */
  public static buildDebtorPortalUrl(agreement: PaymentAgreement): string {
    const slug = this.slugifyConjunto(agreement.copropiedad_nombre);
    return `${window.location.origin}/portal-deudor/${slug}/${agreement.magic_token}`;
  }

  /**
   * Descarga directamente el PDF en el navegador del usuario
   */
  public static downloadAgreementPdf(agreement: PaymentAgreement): void {
    try {
      const doc = this.generateAgreementPdf(agreement);
      const safeUnit = (agreement.unidad_privada || 'unidad').replace(/\s+/g, '_').toLowerCase();
      const fileName = `Acuerdo_Pago_${safeUnit}_${agreement.id || 'doc'}.pdf`;
      doc.save(fileName);
      toast.success(`Acuerdo de pago descargado con éxito: ${fileName}`);
    } catch (err) {
      console.error('Error generating Agreement PDF:', err);
      toast.error('No se pudo generar el documento PDF del acuerdo de pago.');
    }
  }

  /**
   * Retorna el Blob del PDF generado para ser enviado por correo o API
   */
  public static getAgreementPdfBlob(agreement: PaymentAgreement): Blob {
    const doc = this.generateAgreementPdf(agreement);
    return doc.output('blob');
  }

  /**
   * Retorna el Data URI Base64 del PDF
   */
  public static getAgreementPdfBase64(agreement: PaymentAgreement): string {
    const doc = this.generateAgreementPdf(agreement);
    return doc.output('datauristring');
  }

  /**
   * Abre WhatsApp con el resumen legal formal, datos del acuerdo, estado de cuotas y enlace directo al Portal del Deudor
   * incluyendo el nombre del conjunto en la URL amigable
   */
  public static shareAgreementViaWhatsApp(agreement: PaymentAgreement, customPhone?: string): void {
    const targetPhone = customPhone || agreement.telefono || '';
    const cleanTel = targetPhone.replace(/\D/g, '');
    const num = cleanTel.length === 10 ? `57${cleanTel}` : cleanTel;

    const portalLink = this.buildDebtorPortalUrl(agreement);

    const texto = `🏛️ *ADMINISTRACIÓN ${agreement.copropiedad_nombre.toUpperCase()}*\n` +
      `NIT: ${agreement.copropiedad_nit || 'Propiedad Horizontal'}\n\n` +
      `Estimado(a) *${agreement.propietario_nombre}*,\n\n` +
      `Le informamos el estado formal de su *Acuerdo de Pago y Transacción Extrajudicial* para el inmueble *${agreement.unidad_privada}*:\n\n` +
      `📄 *Radicado:* ${agreement.id}\n` +
      `⚖️ *Estado Actual:* *${(agreement.estado || 'ACTIVO').toUpperCase()}*\n` +
      `💰 *Deuda Inicial:* $${agreement.deuda_total_inicial.toLocaleString('es-CO')}\n` +
      `🎁 *Descuento en Intereses:* $${agreement.intereses_condonados.toLocaleString('es-CO')} (${agreement.porcentaje_condonacion_intereses}%)\n` +
      `✅ *Total Pactado:* *$${agreement.deuda_pactada.toLocaleString('es-CO')} COP*\n` +
      `📊 *Progreso:* ${agreement.cuotas_pagadas} de ${agreement.cuotas_totales} cuotas canceladas\n` +
      `📅 *Próximo Vencimiento:* *${agreement.proximo_vencimiento}*\n\n` +
      `📱 *Portal de Autogestión del Deudor:*\n` +
      `Ingrese al enlace para ver el cronograma, descargar el PDF y pagar en línea:\n\n` +
      `${portalLink}\n\n` +
      `Documento con mérito ejecutivo pleno bajo Ley 675 de 2001 y Ley 2300 de 2023.`;

    const waUrl = `https://wa.me/${num}?text=${encodeURIComponent(texto)}`;
    window.open(waUrl, '_blank');
    toast.success('Abriendo WhatsApp para despachar resumen y enlace del portal.');
  }

  /**
   * Copia el enlace directo del portal del deudor al portapapeles incluyendo el conjunto
   */
  public static async copyPortalLink(agreement: PaymentAgreement): Promise<string> {
    const portalLink = this.buildDebtorPortalUrl(agreement);
    try {
      await navigator.clipboard.writeText(portalLink);
      toast.success('Enlace del Portal del Deudor copiado al portapapeles.');
      return portalLink;
    } catch {
      toast.error('No se pudo copiar el enlace.');
      return portalLink;
    }
  }
}
export default PaymentAgreementPdfService;
